import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  CssBaseline,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { styled } from '@mui/system';
import { useAuth } from '../../contexts/AuthContext';
import { usePaddle } from '../../contexts/PaddleContext';
import axiosInstance from '../../actions/utils/axiosInstance';
import StripyBackground from '../login/DiagonalCanvas';

interface TierInfo {
  id: string;
  name: string;
  priceId: string;
  price?: string;
  interval?: string;
  features: string[];
}

interface PricingData {
  paddleEnvironment: string;
  tiers: TierInfo[];
}

const PageContainer = styled(Stack)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(2),
  backgroundColor: 'transparent',
  marginTop: '64px',
  minHeight: 'calc(100vh - 64px)',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
}));

const PricingPage: React.FC = () => {
  const { user, refreshStatus } = useAuth();
  const { paddle, isReady, onCheckoutComplete, offCheckoutComplete } = usePaddle();
  const [tiers, setTiers] = useState<TierInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutInProgress, setCheckoutInProgress] = useState(false);

  useEffect(() => {
    axiosInstance
      .get<{ success: boolean; data: PricingData }>('/subscription/pricing')
      .then((res) => {
        if (res.data?.data?.tiers) setTiers(res.data.data.tiers);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleCheckoutCompleted = useCallback(async () => {
    setCheckoutInProgress(false);
    await refreshStatus();
    window.location.href = '/?subscribed=1';
  }, [refreshStatus]);

  useEffect(() => {
    onCheckoutComplete(handleCheckoutCompleted);
    return () => offCheckoutComplete(handleCheckoutCompleted);
  }, [handleCheckoutCompleted, onCheckoutComplete, offCheckoutComplete]);

  const handleSubscribe = (priceId: string) => {
    if (!paddle || !priceId || !user) return;

    setCheckoutInProgress(true);

    paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      customData: { userId: user.email },
      customer: { email: user.email },
      settings: {
        theme: 'dark',
        successUrl: window.location.origin + '/?subscribed=1',
      },
    });
  };

  return (
    <>
      <StripyBackground />
      <CssBaseline />
      <PageContainer>
        <Box sx={{ maxWidth: 900, width: '100%', textAlign: 'center' }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}
          >
            Choose Your Plan
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
            Get started with RAF Scribe. Cancel anytime.
          </Typography>

          {loading ? (
            <CircularProgress sx={{ mt: 4 }} />
          ) : (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={3}
              justifyContent="center"
              alignItems="stretch"
            >
              {tiers.map((tier, idx) => {
                const isPro = tier.id === 'pro';
                return (
                  <Card
                    key={tier.id}
                    sx={{
                      flex: 1,
                      maxWidth: 400,
                      position: 'relative',
                      border: isPro ? '2px solid' : '1px solid',
                      borderColor: isPro ? 'primary.main' : 'divider',
                      borderRadius: '12px',
                      overflow: 'visible',
                    }}
                  >
                    {isPro && (
                      <Chip
                        label="Most Popular"
                        color="primary"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -12,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontWeight: 600,
                        }}
                      />
                    )}
                    <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                        {tier.name}
                      </Typography>

                      {tier.price && (
                        <Box sx={{ mb: 2 }}>
                          <Typography component="span" variant="h4" sx={{ fontWeight: 800 }}>
                            {tier.price}
                          </Typography>
                          <Typography component="span" variant="body2" color="text.secondary">
                            {' '}/ {tier.interval || 'month'}
                          </Typography>
                        </Box>
                      )}

                      <Divider sx={{ mb: 2 }} />

                      <Box sx={{ flex: 1, textAlign: 'left', mb: 3 }}>
                        {tier.features.map((feature) => (
                          <Stack
                            key={feature}
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mb: 1 }}
                          >
                            <CheckIcon fontSize="small" color="success" />
                            <Typography variant="body2">{feature}</Typography>
                          </Stack>
                        ))}
                      </Box>

                      <Button
                        variant={isPro ? 'contained' : 'outlined'}
                        size="large"
                        fullWidth
                        disabled={!isReady || !tier.priceId || checkoutInProgress}
                        onClick={() => handleSubscribe(tier.priceId)}
                      >
                        {checkoutInProgress ? (
                          <CircularProgress size={22} color="inherit" />
                        ) : (
                          `Subscribe to ${tier.name}`
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}

          <Typography variant="body2" sx={{ mt: 4, color: 'text.secondary' }}>
            Secure payment powered by Paddle. All prices include applicable taxes.
          </Typography>
        </Box>
      </PageContainer>
    </>
  );
};

export default PricingPage;
